import type { ComponentProps } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@reflex/ui/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@reflex/ui/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { mutateWithToast } from '@/lib/mutation-helpers';

interface DeclineInviteButtonProps {
  inviteId: string;
  organizationName: string;
  /**
   * Fired after a successful decline so the parent can refetch its
   * pending-invite list or move the user off a now-terminal page.
   */
  onDeclined?: () => void;
  triggerLabel?: string;
  triggerVariant?: ComponentProps<typeof Button>['variant'];
  triggerSize?: ComponentProps<typeof Button>['size'];
  /**
   * Base test id. The trigger / confirm / cancel controls derive their
   * own ids from it so a list of invites can scope assertions per-row.
   */
  testId?: string;
  className?: string;
}

/**
 * "Decline" control for a pending invite, guarded by an `AlertDialog`
 * confirm so an accidental click can't burn the invite (declining is a
 * one-time terminal action — rejoining needs a fresh admin invite). On
 * confirm it posts to `POST /invites/:inviteId/decline` (authed +
 * email-gated server-side) and calls {@link DeclineInviteButtonProps.onDeclined}
 * so the caller can refresh.
 */
export function DeclineInviteButton({
  inviteId,
  organizationName,
  onDeclined,
  triggerLabel = 'Decline',
  triggerVariant = 'ghost',
  triggerSize = 'sm',
  testId = 'invite-decline',
  className,
}: DeclineInviteButtonProps) {
  const declineMutation = useMutation({
    mutationFn: () => api.declineInvite(inviteId),
    onSuccess: () => onDeclined?.(),
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant={triggerVariant}
          size={triggerSize}
          disabled={declineMutation.isPending}
          aria-label={`Decline invite to ${organizationName}`}
          data-testid={`${testId}-trigger`}
          className={className}
        >
          {triggerLabel}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Decline invite to {organizationName}?</AlertDialogTitle>
          <AlertDialogDescription>
            You&apos;ll need a fresh invite from an admin to join {organizationName} later. This
            can&apos;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Keep invite</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            data-testid={`${testId}-confirm`}
            onClick={() =>
              mutateWithToast(declineMutation, undefined, {
                success: `Declined invite to ${organizationName}`,
                error: 'Failed to decline invite',
              })
            }
          >
            Decline invite
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
